import { Injectable } from '@angular/core';
import { FlowPageOrder } from './flow-page-order.class';
import { dependencies } from './workflow.dependency';
import { Workflow } from './workflow.type';
import { PageInstance, PageService } from './page.service';
import { ActivatedRouteSnapshot } from '@angular/router';
import { PageUrlHelper } from './page-url-helper.class';
import { registerDevtoolsState } from '@shared/service/devtools/devtools-state-registry.service';

@Injectable({
  providedIn: 'root'
})
export class FlowService {
  /**
   * Flow instances
   */
  private instances: { [id: string]: FlowInstance } = {};
  constructor(private readonly pageService: PageService) {
    registerDevtoolsState(this, 'FlowService');
  }

  public createFlow(id: string): FlowInstance {
    if (this.instances[id]) {
      return this.instances[id];
    }

    const newInstance = new FlowInstance(this.pageService, id);

    this.instances[id] = newInstance;
    newInstance.config = dependencies.config.flows[id];

    return newInstance;
  }

  /**
   * Test configuration values for validity + set sane defaults (e.g. adding implicit IDs, etc)
   * Throws errors on integration fails
   * @param id workflow ID from `workflows.json`
   */
  public test(id: string): void {
    const config = dependencies.config.flows[id];
    // pages syntax and integrity with `workflow-pages.json`
    if (!Array.isArray(config.pages)) {
      throw new Error(`The "pages" property is missing or not an array on workflow ${id}: ${JSON.stringify(config.pages)}`);
    }
    for (const pageId of config.pages) {
      if (typeof pageId !== 'string' || !pageId) {
        throw new Error(`A pageId is invalid on workflow ${id}: ${JSON.stringify(pageId)}`);
      }
      if (!dependencies.config.pages[pageId]) {
        throw new Error(`A pageId referenced on workflow ${id} does not exist in workflow-pages: "${pageId}"`);
      }
    }
  }

  public ensureConfig(configId: string): void {
    const config = dependencies.config.flows[configId];
    if (!config) {
      return;
    }

    // ensure consistency with IDs
    config.id = configId;

    // implicit path
    config.path = config.path || configId;
  }

  // This is a stopgap to avoid too many refactors at one time.
  // I think the pageUrlHelper should just be converted to a service, but these inputs need to be handled differently
  // TODO: Finish this.
  public createPageUrlHelper(activatedRouteSnapshot: ActivatedRouteSnapshot, baseUrl: string = '/'): PageUrlHelper {
    return new PageUrlHelper(this, this.pageService, activatedRouteSnapshot, baseUrl);
  }
}

// ********************************************************************************
/**
 * Flow specific logic and configuration store
 */
export class FlowInstance {
  /**
   * Ordered list of page objects for the page
   */
  readonly #pages: PageInstance[] = [];

  /**
   * Lock `#pages` array. This is to prevent redundant calls for the `.pages` getter to trigger nested cycles
   */
  #pagesLock = false;

  /**
   * Workflow config as specified at `workflows.json`
   */
  public config: Workflow;

  /** loader to entire flow subject */
  #loader = false;

  /**
   * Unique flow instance
   * @param id unique container id
   */
  constructor(private readonly pageService: PageService, public readonly id: string) {}

  /** Getter of page loader flag  */
  public get loader(): boolean {
    return this.#loader;
  }
  /**
   * Ordered list of visible pages in the flow
   */
  public get pages(): PageInstance[] {
    const pages = this.allPages.filter((page) => page.visible);
    this.setPreviousAndNextPages(pages);
    return pages;
  }

  /**
   * list of visible pages that can be shown in review
   */
  public get reviewablePages(): PageInstance[] {
    const pages = this.allPages.filter((page) => page.visible && !page.config.excludedInFlow && !page.hideInReview);
    return pages;
  }

  /**
   * Ordered list of all pages regardless if they are visible or not
   */
  public get allPages(): PageInstance[] {
    if (this.#pagesLock) {
      return this.#pages;
    }

    this.#pagesLock = true;
    this.#pages.length = 0;

    this.#pages.push(...new FlowPageOrder(this.pageService, this.config, this.id).pagesWithOrder());

    this.#pagesLock = false;
    return this.#pages;
  }

  public get isCompleted(): boolean {
    // eslint-disable-next-line eqeqeq
    return this.pages.filter((p) => !p.helper.isPageComplete).length == 0;
  }

  /**
   * on page load
   */
  public onPageLoad(): void {
    dependencies.logicService.evaluate(this.config.onPageLoad, { flow: this }, true);
  }

  private setPreviousAndNextPages(pages: PageInstance[]): void {
    for (let i = 0; i < pages.length; i++) {
      pages[i].nextPage = pages[i + 1];
      pages[i].previousPage = pages[i - 1];
      pages[i].defaultPageType = this.config.defaultPageType;
    }
  }

  /** Enables a loader in the workflow hides all pages and routings */
  public showLoader() {
    this.#loader = true;
  }

  /** Disables the loader in the workflow*/
  public closeLoader() {
    this.#loader = false;
  }
}
